import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'
import { type ButtonHTMLAttributes, forwardRef } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'nav'
    | 'ghost'
    | 'destructive'
    | 'silver'
    | 'default'
    | 'outline'
    | 'link'
    | 'accent'
    | 'mobileSuggestion'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'nav' | 'header' | 'icon' | 'icon-sm' | 'icon-lg'
  isLoading?: boolean
  loading?: boolean
  asChild?: boolean
}

export function buttonVariants({ variant = 'primary', size = 'md' }: Pick<ButtonProps, 'variant' | 'size'> = {}) {
  const resolvedVariant = variant === 'default' ? 'silver' : variant
  return cn(
    'inline-flex items-center justify-center gap-2',
    'font-medium cursor-pointer select-none whitespace-nowrap leading-none',
    'rounded-[var(--radius-pill)] transition-all duration-[time:80ms] ease-[timing-function:var(--ease-out)]',
    'disabled:opacity-40 disabled:pointer-events-none',

    size === 'xs' && 'h-7 px-3 text-[12px]',
    size === 'sm' && 'h-8 px-4 text-[13px]',
    size === 'nav' && 'h-9 px-4 text-[14px]',
    size === 'md' && 'h-10 px-5 text-[15px]',
    size === 'lg' && 'h-11 px-8 text-[16px]',
    size === 'header' && 'h-9 px-5 text-[13px]',
    size === 'icon' && 'h-9 w-9 p-0',
    size === 'icon-sm' && 'h-7 w-7 p-0 text-[11px]',
    size === 'icon-lg' && 'h-11 w-11 p-0 text-[14px]',

    resolvedVariant === 'primary' && 'bg-white text-black hover:bg-white/90 active:bg-white/80 active:scale-[0.98] hover:shadow-[0_0_16px_var(--accent-glow)]',
    resolvedVariant === 'secondary' && 'bg-[var(--bg-overlay)] text-[var(--text-primary)] border border-[var(--border-default)] hover:bg-[var(--bg-elevated)] hover:border-[var(--border-strong)] active:scale-[0.98]',
    resolvedVariant === 'nav' && 'bg-[var(--bg-overlay)] text-[var(--text-primary)] border border-[var(--border-default)] hover:bg-[var(--bg-elevated)]',
    resolvedVariant === 'ghost' && 'bg-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]',
    resolvedVariant === 'destructive' && 'bg-[var(--error-muted)] text-[var(--error)] border border-[var(--error-border)] hover:bg-[var(--error-muted)]',
    resolvedVariant === 'silver' && 'bg-white text-black hover:bg-white/90',
    resolvedVariant === 'accent' && 'bg-white text-black hover:bg-white/90',
    resolvedVariant === 'outline' && 'bg-transparent border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
    resolvedVariant === 'link' && 'bg-transparent h-auto p-0 text-[var(--text-secondary)] hover:text-[var(--text-primary)] underline-offset-4 hover:underline',
    resolvedVariant === 'mobileSuggestion' && 'bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]',
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', isLoading, loading, asChild = false, className, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        disabled={disabled || isLoading || loading}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {isLoading || loading ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin-slow" /> : children}
      </Comp>
    )
  },
)
Button.displayName = 'Button'
